/**
 * Campaign edit access rules.
 *
 * Customers (self-service advertisers) may edit a campaign order only while
 * it is in the building/requesting phase. Once an order is approved for
 * launch or in-flight, editing is restricted to users with the `isProcessor`
 * permission (deliberately NOT the usual isTrafficker||isProcessor Ad-Ops
 * pair — traffickers keep pre-launch access only).
 *
 * Enforcement is frontend-only; see docs/campaign-creation-and-approval-flow.md.
 */

/**
 * Statuses in which anyone who can reach the edit UI may still edit.
 * PENDING_CLIENT is the legacy audit flow's "changes requested" state — same
 * customer-acts semantics as REJECTED.
 */
export const CUSTOMER_EDITABLE_STATUSES = ['DRAFT', 'PENDING', 'PENDING_CLIENT', 'REJECTED'];

export const isCustomerEditableStatus = (status) => CUSTOMER_EDITABLE_STATUSES.includes(status);

/**
 * @param {string} status – campaign order status
 * @param {Object} options
 * @param {boolean} [options.isProcessor] – from isAuthorized(['isProcessor'], [])
 * @returns {boolean} whether the current user may edit the order
 */
export const canEditCampaignOrder = (status, { isProcessor } = {}) =>
  isCustomerEditableStatus(status) || !!isProcessor;

/**
 * Whether an order is KNOWN to be locked for the current session.
 *
 * Deliberately "known to be locked", not "not known to be editable": currentUser
 * and the campaign order resolve independently, and treating the in-between
 * window as locked bounces processors out of the editor mid-load.
 *
 * Reads currentUser.permission rather than the async usePermify isAuthorized():
 * effects that depend only on that stable callback run once at mount and can
 * observe localStorage.__permifyUser before AccountLoader writes it, latching a
 * spurious `false` forever. currentUser.permission carries the same flags —
 * AccountLoader sets permission from the org config and derives the permify
 * roles from it, and preview sessions build both from computePreviewAccess() —
 * so it is equivalent for isProcessor in normal, faithful and work-as-parent
 * sessions, without the race.
 *
 * @param {Object} args
 * @param {Object} [args.currentUser] – Apollo currentUser (note: the cache is
 *        seeded with a non-null guest object whose permission is null, so
 *        presence of currentUser alone does NOT mean permissions are loaded)
 * @param {Object} [args.campaignOrder] – the campaign order being opened
 * @returns {boolean} true only when permissions AND the order are both known
 *          and the order's status is not editable by this session
 */
export const isOrderLocked = ({ currentUser, campaignOrder } = {}) => {
  const permission = currentUser && currentUser.permission;
  if (!permission || !campaignOrder) return false;
  return !canEditCampaignOrder(campaignOrder.status, {
    isProcessor: !!permission.isProcessor,
  });
};
